import React, { useState } from 'react';
import CDSInfoModal from '../../UI/CDSInfoModal';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';

/**
 * Get color based on no-show percentage severity.
 */
const getNoShowColor = (pct) => {
  if (pct < 25) return '#22c55e';
  if (pct < 35) return '#f59e0b';
  if (pct <= 50) return '#f472b6';
  return '#ef4444';
};

const getRiskLevel = (pct) => {
  if (pct > 50) return 'Critical';
  if (pct >= 35) return 'High Risk';
  if (pct >= 25) return 'Moderate Risk';
  return 'Low Risk';
};

/**
 * Process raw form data into chart-ready format.
 */
export const processChartData = (rawData) => {
  if (!rawData || rawData.length === 0) return null;

  const chartItems = rawData
    .map(d => ({
      clientId: d.clientId,
      totalAppointments: d.totalAppointments,
      noShows: d.noShows,
      noShowPct: d.totalAppointments > 0
        ? Math.round((d.noShows / d.totalAppointments) * 1000) / 10
        : 0,
      attendedPct: d.totalAppointments > 0
        ? Math.round(((d.totalAppointments - d.noShows) / d.totalAppointments) * 1000) / 10
        : 100
    }))
    .sort((a, b) => b.noShowPct - a.noShowPct);

  const totalClients = chartItems.length;
  const totalAppointments = chartItems.reduce((sum, d) => sum + d.totalAppointments, 0);
  const totalNoShows = chartItems.reduce((sum, d) => sum + d.noShows, 0);
  const practiceNoShowRate = totalAppointments > 0
    ? Math.round((totalNoShows / totalAppointments) * 1000) / 10
    : 0;

  const critical = chartItems.filter(d => d.noShowPct > 50);
  const highRisk = chartItems.filter(d => d.noShowPct >= 35 && d.noShowPct <= 50);
  const moderateRisk = chartItems.filter(d => d.noShowPct >= 25 && d.noShowPct < 35);
  const lowRisk = chartItems.filter(d => d.noShowPct < 25);

  const topNoShowClients = chartItems.slice(0, 3);

  return {
    chartItems,
    totalClients,
    totalAppointments,
    totalNoShows,
    practiceNoShowRate,
    attendanceRate: Math.round((100 - practiceNoShowRate) * 10) / 10,
    criticalCount: critical.length,
    criticalPct: totalClients > 0 ? Math.round((critical.length / totalClients) * 1000) / 10 : 0,
    highRiskCount: highRisk.length,
    highRiskPct: totalClients > 0 ? Math.round((highRisk.length / totalClients) * 1000) / 10 : 0,
    moderateRiskCount: moderateRisk.length,
    lowRiskCount: lowRisk.length,
    topNoShowClients
  };
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-bold text-gray-900">{d.clientId}</p>
      <p className="text-gray-700">No-Show Rate: <span className="font-semibold">{d.noShowPct}%</span></p>
      <p className="text-gray-500 text-xs">{d.noShows} of {d.totalAppointments} appointments missed</p>
      <p className="text-xs mt-1" style={{ color: getNoShowColor(d.noShowPct) }}>
        {getRiskLevel(d.noShowPct)}
      </p>
    </div>
  );
};

const NoShowChart = ({ chartData, pdfMode = false }) => {
  const [showCDSModal, setShowCDSModal] = useState(false);
  if (!chartData) return null;

  const { chartItems, practiceNoShowRate, totalClients, attendanceRate,
    criticalCount, highRiskCount, moderateRiskCount, lowRiskCount } = chartData;

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <CDSInfoModal isOpen={showCDSModal} onClose={() => setShowCDSModal(false)} chartType="noshow" />
      {!pdfMode && (
        <>
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-xl font-bold text-gray-900">No-Show Percentage by Client</h2>
            <button
              onClick={() => setShowCDSModal(true)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium border border-blue-200 rounded px-2 py-1 hover:bg-blue-50 transition-colors"
            >
              ⓘ View Logic
            </button>
          </div>
          <p className="text-gray-600 text-sm mb-2">Attendance pattern analysis across your caseload</p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 mb-4">
            <p className="text-xs text-yellow-800">Clinical Decision Support Notice: This tool provides informational analysis to support clinical decision-making. All findings must be reviewed and interpreted by a licensed healthcare professional.</p>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-5 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 font-semibold">Practice No-Show Rate</p>
              <p className="text-xl font-bold text-blue-700">{practiceNoShowRate}%</p>
            </div>
            <div className="bg-red-100 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 font-semibold">Critical (&gt;50%)</p>
              <p className="text-xl font-bold text-red-600">{criticalCount}</p>
            </div>
            <div className="bg-pink-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 font-semibold">High Risk (35-50%)</p>
              <p className="text-xl font-bold text-pink-500">{highRiskCount}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 font-semibold">Moderate (25-35%)</p>
              <p className="text-xl font-bold text-yellow-600">{moderateRiskCount}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 font-semibold">Low Risk (&lt;25%)</p>
              <p className="text-xl font-bold text-green-600">{lowRiskCount}</p>
            </div>
          </div>
        </>
      )}

      {/* Chart */}
      <ResponsiveContainer width="100%" height={Math.max(300, chartItems.length * 40)}>
        <BarChart
          data={chartItems}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            domain={[0, 100]}
            tickFormatter={v => `${v}%`}
            label={{ value: 'No-Show Percentage (%)', position: 'insideBottom', offset: -5 }}
          />
          <YAxis
            type="category"
            dataKey="clientId"
            width={100}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine x={25} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: '25%', position: 'top', fontSize: 10 }} />
          <ReferenceLine x={35} stroke="#f472b6" strokeDasharray="3 3" label={{ value: '35%', position: 'top', fontSize: 10 }} />
          <ReferenceLine x={50} stroke="#ef4444" strokeDasharray="3 3" label={{ value: '50%', position: 'top', fontSize: 10 }} />
          <Bar dataKey="noShowPct" name="No-Show %" radius={[0, 4, 4, 0]}>
            {chartItems.map((entry, index) => (
              <Cell key={index} fill={getNoShowColor(entry.noShowPct)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      {pdfMode ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '12px' }}>
          {[
            { color: '#22c55e', label: 'Low Risk (<25%)' },
            { color: '#f59e0b', label: 'Moderate (25-35%)' },
            { color: '#f472b6', label: 'High (35-50%)' },
            { color: '#ef4444', label: 'Critical (>50%)' }
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: item.color, flexShrink: 0 }} />
              <span style={{ fontSize: '13px', color: '#4b5563' }}>{item.label}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-center gap-4 mt-3 text-sm">
          {[
            { color: '#22c55e', label: 'Low Risk (<25%)' },
            { color: '#f59e0b', label: 'Moderate (25-35%)' },
            { color: '#f472b6', label: 'High (35-50%)' },
            { color: '#ef4444', label: 'Critical (>50%)' }
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-1">
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: item.color, flexShrink: 0 }} />
              <span className="text-xs text-gray-600">{item.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NoShowChart;
